// Social profiles and their dated measurements (competitor_social_snapshots).
import type { Identifier } from "ra-core";

import {
  isInvalidAmount,
  optionalText,
  optionalUrl,
  parseAmount,
  parseOptionalDay,
  type FormResult,
} from "./forms";
import { isPlatform, PLATFORMS, profileUrlOf } from "./platforms";
import type {
  CompetitorSocialProfile,
  CompetitorSocialSnapshot,
  SocialPlatform,
} from "./types";

/** Oldest first (the order of a chart); same day: the newest row last. */
export const sortSnapshots = (
  snapshots: readonly CompetitorSocialSnapshot[],
): CompetitorSocialSnapshot[] =>
  [...snapshots].sort(
    (a, b) =>
      a.measured_at.localeCompare(b.measured_at) || Number(a.id) - Number(b.id),
  );

/** Each profile's snapshots, oldest first. */
export const snapshotsByProfile = (
  snapshots: readonly CompetitorSocialSnapshot[],
): Map<string, CompetitorSocialSnapshot[]> => {
  const grouped = new Map<string, CompetitorSocialSnapshot[]>();
  for (const snapshot of sortSnapshots(snapshots)) {
    const key = String(snapshot.profile_id);
    grouped.set(key, [...(grouped.get(key) ?? []), snapshot]);
  }
  return grouped;
};

type Metric = "followers" | "posts_count" | "following_count";

export interface ProfileSummary {
  latest: CompetitorSocialSnapshot | null;
  previous: CompetitorSocialSnapshot | null;
  /** Latest minus the previous measurement that has the value; null if unknown. */
  deltas: Record<Metric, number | null>;
  /** Followers over time, oldest first (measurements without it skipped). */
  followersSeries: { day: string; value: number }[];
}

const delta = (
  history: readonly CompetitorSocialSnapshot[],
  metric: Metric,
): number | null => {
  const known = history.filter((snapshot) => snapshot[metric] != null);
  if (known.length < 2) return null;
  const last = known[known.length - 1];
  // Only against an earlier measurement than the latest one overall.
  if (last !== history[history.length - 1]) return null;
  return Number(last[metric]) - Number(known[known.length - 2][metric]);
};

/** What a profile row shows: its latest measurement and its evolution. */
export const summarizeProfile = (
  history: readonly CompetitorSocialSnapshot[],
): ProfileSummary => {
  const sorted = sortSnapshots(history);
  return {
    latest: sorted[sorted.length - 1] ?? null,
    previous: sorted[sorted.length - 2] ?? null,
    deltas: {
      followers: delta(sorted, "followers"),
      posts_count: delta(sorted, "posts_count"),
      following_count: delta(sorted, "following_count"),
    },
    followersSeries: sorted
      .filter((snapshot) => snapshot.followers != null)
      .map((snapshot) => ({
        day: snapshot.measured_at,
        value: Number(snapshot.followers),
      })),
  };
};

/** Profiles in platform order, then by handle. */
export const sortProfiles = (
  profiles: readonly CompetitorSocialProfile[],
): CompetitorSocialProfile[] => {
  const rank = (platform: SocialPlatform) => {
    const index = PLATFORMS.indexOf(platform);
    return index === -1 ? PLATFORMS.length : index;
  };
  return [...profiles].sort(
    (a, b) =>
      rank(a.platform) - rank(b.platform) || a.handle.localeCompare(b.handle),
  );
};

/** Number of profiles per competitor id. */
export const countByCompetitor = (
  rows: readonly { competitor_id: Identifier }[],
): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const key = String(row.competitor_id);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
};

/**
 * SVG polyline points of a sparkline (width x height box, 2px inset). One
 * value draws a flat line; an empty series none.
 */
export const sparklinePoints = (
  values: readonly number[],
  width: number,
  height: number,
): string => {
  if (values.length === 0) return "";
  const series = values.length === 1 ? [values[0], values[0]] : values;
  const min = Math.min(...series);
  const max = Math.max(...series);
  const inset = 2;
  const span = max - min || 1;
  return series
    .map((value, index) => {
      const x = inset + (index * (width - 2 * inset)) / (series.length - 1);
      const y =
        max === min
          ? height / 2
          : inset + ((max - value) * (height - 2 * inset)) / span;
      return `${Math.round(x * 10) / 10},${Math.round(y * 10) / 10}`;
    })
    .join(" ");
};

// ---------- forms ----------

export interface ProfileFormValues {
  platform: SocialPlatform;
  handle: string;
  url: string;
  notes: string;
}

export type ProfilePayload = Pick<
  CompetitorSocialProfile,
  "competitor_id" | "platform" | "handle" | "url" | "notes"
>;

export const profileFormDefaults = (
  profile?: Partial<CompetitorSocialProfile>,
): ProfileFormValues => ({
  platform: isPlatform(profile?.platform) ? profile.platform : "instagram",
  handle: profile?.handle ?? "",
  url: profile?.url ?? "",
  notes: profile?.notes ?? "",
});

export const normalizeProfileForm = (
  competitorId: Identifier,
  values: ProfileFormValues,
): FormResult<ProfilePayload> => {
  const handle = values.handle.trim();
  if (!handle)
    return { ok: false, error: "competition.social.form.handle_required" };
  if (handle.length > 120)
    return { ok: false, error: "competition.social.form.handle_too_long" };
  const platform = isPlatform(values.platform) ? values.platform : "otro";
  return {
    ok: true,
    data: {
      competitor_id: competitorId,
      platform,
      handle,
      url: optionalUrl(values.url) ?? profileUrlOf(platform, handle),
      notes: optionalText(values.notes),
    },
  };
};

const SNAPSHOT_NUMBERS = [
  "followers",
  "posts_count",
  "following_count",
  "posts_per_week",
  "engagement_rate",
] as const;

type SnapshotNumber = (typeof SNAPSHOT_NUMBERS)[number];

const INTEGER_FIELDS: readonly SnapshotNumber[] = [
  "followers",
  "posts_count",
  "following_count",
];

export type SnapshotFormValues = Record<SnapshotNumber, string> & {
  measured_at: string;
  source: string;
  notes: string;
};

export type SnapshotPayload = Pick<
  CompetitorSocialSnapshot,
  "profile_id" | "measured_at" | "source" | "notes" | "sales_id"
> &
  Record<SnapshotNumber, number | null>;

export const snapshotFormDefaults = (
  today: string,
  snapshot?: Partial<CompetitorSocialSnapshot>,
  toInput: (value: number | null | undefined) => string = (value) =>
    value == null ? "" : String(value),
): SnapshotFormValues => ({
  measured_at: snapshot?.measured_at ?? today,
  followers: toInput(snapshot?.followers),
  posts_count: toInput(snapshot?.posts_count),
  following_count: toInput(snapshot?.following_count),
  posts_per_week: toInput(snapshot?.posts_per_week),
  engagement_rate: toInput(snapshot?.engagement_rate),
  source: snapshot?.source ?? "manual",
  notes: snapshot?.notes ?? "",
});

export const normalizeSnapshotForm = (
  profileId: Identifier,
  values: SnapshotFormValues,
  salesId?: Identifier | null,
): FormResult<SnapshotPayload> => {
  const day = parseOptionalDay(values.measured_at);
  if (!day.ok || !day.day)
    return {
      ok: false,
      error: "competition.social.snapshot_form.date_invalid",
    };
  const numbers = {} as Record<SnapshotNumber, number | null>;
  for (const field of SNAPSHOT_NUMBERS) {
    const amount = parseAmount(values[field], INTEGER_FIELDS.includes(field));
    if (isInvalidAmount(amount))
      return {
        ok: false,
        error: INTEGER_FIELDS.includes(field)
          ? "competition.social.snapshot_form.integer_invalid"
          : "competition.social.snapshot_form.number_invalid",
      };
    numbers[field] = amount;
  }
  if (SNAPSHOT_NUMBERS.every((field) => numbers[field] === null))
    return { ok: false, error: "competition.social.snapshot_form.empty" };
  return {
    ok: true,
    data: {
      profile_id: profileId,
      measured_at: day.day,
      ...numbers,
      source: optionalText(values.source),
      notes: optionalText(values.notes),
      // The DB trigger fills it too; sending it keeps the demo (FakeRest) honest.
      sales_id: salesId ?? null,
    },
  };
};
